import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from '../api';
import { useAdmin } from '../context/AdminContext';
import OrderDetail from '../components/OrderDetail';
import './AdminDashboard.css';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: 'badge-pending' },
  { value: 'accepted', label: 'Accepted', color: 'badge-accepted' },
  { value: 'quoted', label: 'Quote Sent', color: 'badge-quoted' },
  { value: 'payment_accepted', label: 'Payment Accepted', color: 'badge-payment_accepted' },
  { value: 'shipping', label: 'Shipping Out', color: 'badge-shipping' },
];

const COURIERS = ['EMS', 'FEDEX'];

export default function AdminDashboard() {
  const { admin, logout } = useAdmin();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [quoteModal, setQuoteModal] = useState(null);
  const [quoteForm, setQuoteForm] = useState({ quote_amount: '', quote_currency: 'USD', message: '' });
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteResult, setQuoteResult] = useState(null);
  const [notification, setNotification] = useState(null);

  const LIMIT = 15;

  useEffect(() => {
    if (!admin) navigate('/admin/login');
  }, [admin, navigate]);

  const fetchOrders = useCallback(async () => {
    if (!admin) return;
    setLoading(true);
    try {
      const { data } = await axios.get('/api/admin/orders', {
        params: { status: statusFilter, search, page, limit: LIMIT }
      });
      setOrders(data.orders);
      setTotal(data.total);
    } catch (err) {
      if (err.response?.status === 401) { logout(); navigate('/admin/login'); }
    } finally { setLoading(false); }
  }, [admin, statusFilter, search, page, logout, navigate]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const showNotif = (msg, type = 'success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this order?')) return;
    try {
      await axios.delete(`/api/admin/orders/${id}`);
      showNotif('Order deleted');
      fetchOrders();
      if (detailOpen && selected?.id === id) setDetailOpen(false);
    } catch { showNotif('Delete failed', 'error'); }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await axios.put(`/api/admin/orders/${id}`, { ...orders.find(o => o.id === id), status });
      showNotif('Status updated');
      fetchOrders();
      if (selected?.id === id) {
        const { data } = await axios.get(`/api/admin/orders/${id}`);
        setSelected(data);
      }
    } catch { showNotif('Update failed', 'error'); }
  };

  const openDetail = async (id) => {
    try {
      const { data } = await axios.get(`/api/admin/orders/${id}`);
      setSelected(data);
      setDetailOpen(true);
    } catch { showNotif('Failed to load order', 'error'); }
  };

  const openQuote = (order) => {
    setQuoteModal(order);
    setQuoteResult(null);
    setQuoteForm({ quote_amount: order.quote_amount || '', quote_currency: 'USD', message: '' });
  };

  const sendQuote = async () => {
    if (!quoteForm.quote_amount || parseFloat(quoteForm.quote_amount) <= 0) {
      showNotif('Enter a valid quote amount', 'error');
      return;
    }
    setQuoteLoading(true);
    setQuoteResult(null);
    try {
      const { data } = await axios.post(`/api/admin/orders/${quoteModal.id}/send-quote`, quoteForm);
      setQuoteResult({ success: true, data });
      showNotif(`PayPal invoice sent to ${quoteModal.customer_email}`);
      fetchOrders();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to send quote';
      setQuoteResult({ success: false, msg });
      showNotif(msg, 'error');
    } finally { setQuoteLoading(false); }
  };

  const closeQuoteModal = () => {
    setQuoteModal(null);
    setQuoteResult(null);
  };

  const totalPages = Math.ceil(total / LIMIT);
  const getStatusInfo = (val) => STATUS_OPTIONS.find(s => s.value === val) || STATUS_OPTIONS[0];
  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  if (!admin) return null;

  return (
    <div className="admin-page">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="sidebar-logo">
          <div className="nav-logo">
            <img src="/buykorea-logo.png" alt="BuyKorea Logo" style={{ height: '30px', objectFit: 'contain' }} />
          </div>
          <span className="admin-label">Admin</span>
        </div>
        <nav className="sidebar-nav">
          <div className="nav-item active"><span>📦</span> Orders</div>
        </nav>
        <div className="sidebar-user">
          <div className="user-avatar">{admin.username?.charAt(0).toUpperCase()}</div>
          <div>
            <div className="user-name">{admin.username}</div>
            <button className="logout-btn" onClick={() => { logout(); navigate('/admin/login'); }}>Logout</button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="admin-main">
        {notification && (
          <div className={`admin-notif alert-${notification.type}`}>{notification.msg}</div>
        )}

        <div className="admin-header">
          <div>
            <h1>Order Management</h1>
            <p>{total} total orders</p>
          </div>
        </div>

        <div className="status-tabs">
          <button className={`status-tab ${statusFilter === 'all' ? 'active' : ''}`} onClick={() => { setStatusFilter('all'); setPage(1); }}>
            All <span className="tab-count">{total}</span>
          </button>
          {STATUS_OPTIONS.map(s => (
            <button key={s.value} className={`status-tab ${statusFilter === s.value ? 'active' : ''}`} onClick={() => { setStatusFilter(s.value); setPage(1); }}>
              {s.label}
            </button>
          ))}
        </div>

        <div className="search-bar">
          <span className="search-icon">🔍</span>
          <input type="text" placeholder="Search by order #, name, or email..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>

        <div className="orders-table-wrap">
          {loading ? (
            <div className="table-loading">
              <div className="spinner" style={{ borderColor: 'rgba(0,0,0,0.1)', borderTopColor: 'var(--gold-dark)' }}></div>
            </div>
          ) : orders.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📭</div>
              <p>No orders found</p>
            </div>
          ) : (
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Order #</th><th>Customer</th><th>Product</th><th>Qty</th>
                  <th>Status</th><th>Quote</th><th>Date</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(order => {
                  const si = getStatusInfo(order.status);
                  return (
                    <tr key={order.id} className="order-row" onClick={() => openDetail(order.id)}>
                      <td><span className="order-num-cell">{order.order_number}</span></td>
                      <td>
                        <div className="customer-cell">
                          <span className="customer-name">{order.customer_name}</span>
                          <span className="customer-email">{order.customer_email}</span>
                        </div>
                      </td>
                      <td>
                        <div className="product-cell">
                          {order.image_path && <img src={order.image_path} alt="" className="product-thumb" />}
                          <span className="product-desc">{order.product_description?.slice(0, 50) || order.product_url?.slice(0, 40) || '—'}</span>
                        </div>
                      </td>
                      <td>{order.quantity}</td>
                      <td onClick={e => e.stopPropagation()}>
                        <select className={`status-select badge ${si.color}`} value={order.status} onChange={e => handleStatusChange(order.id, e.target.value)}>
                          {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </td>
                      <td>
                        {order.quote_amount
                          ? <span className="quote-amount">${Number(order.quote_amount).toLocaleString()} {order.quote_currency}</span>
                          : <span className="no-quote">—</span>}
                      </td>
                      <td><span className="date-cell">{formatDate(order.created_at)}</span></td>
                      <td onClick={e => e.stopPropagation()}>
                        <div className="action-btns">
                          <button className="btn btn-sm btn-outline action-btn" onClick={() => openQuote(order)} title="Send PayPal Invoice">💬</button>
                          <button className="btn btn-sm btn-danger action-btn" onClick={() => handleDelete(order.id)} title="Delete">🗑</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button className="btn btn-sm btn-outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span className="page-info">Page {page} of {totalPages}</span>
            <button className="btn btn-sm btn-outline" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        )}
      </main>

      {/* Order Detail Drawer */}
      {detailOpen && selected && (
        <OrderDetail
          order={selected}
          onClose={() => setDetailOpen(false)}
          onUpdate={fetchOrders}
          onDelete={handleDelete}
          onQuote={openQuote}
          statusOptions={STATUS_OPTIONS}
          couriers={COURIERS}
          showNotif={showNotif}
        />
      )}

      {/* Quote / PayPal Invoice Modal */}
      {quoteModal && (
        <div className="modal-overlay" onClick={closeQuoteModal}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <span style={{ marginRight: 8 }}>💳</span>
                Send PayPal Invoice
              </h3>
              <button className="modal-close" onClick={closeQuoteModal}>✕</button>
            </div>

            <div className="modal-body">
              {/* PayPal badge */}
              <div className="paypal-invoice-badge">
                <span className="pp-badge-text">
                  <span style={{ color: '#003087', fontWeight: 800 }}>Pay</span><span style={{ color: '#009cde', fontWeight: 800 }}>Pal</span>
                  &nbsp;Invoice
                </span>
                <span className="pp-badge-sub">🔒 Buyer Protection included</span>
              </div>

              <p className="modal-to">To: <strong>{quoteModal.customer_email}</strong></p>
              <p className="modal-order">Order: {quoteModal.order_number}</p>

              {/* Success result */}
              {quoteResult?.success && (
                <div className="quote-success-box">
                  <div className="quote-success-icon">✓</div>
                  <div>
                    <strong>PayPal invoice sent!</strong>
                    <p>Customer will receive a PayPal payment link via email.</p>
                    {quoteResult.data?.paypal_invoice_id && (
                      <p className="invoice-id">Invoice ID: {quoteResult.data.paypal_invoice_id}</p>
                    )}
                  </div>
                </div>
              )}

              {!quoteResult?.success && (
                <>
                  <div className="form-group">
                    <label>Invoice Amount <span className="required">*</span></label>
                    <div className="amount-row">
                      <select
                        value={quoteForm.quote_currency}
                        onChange={e => setQuoteForm(f => ({ ...f, quote_currency: e.target.value }))}
                        style={{ width: '90px' }}
                      >
                        <option value="USD">$ USD</option>
                        <option value="EUR">€ EUR</option>
                        <option value="GBP">£ GBP</option>
                        <option value="AUD">A$ AUD</option>
                        <option value="CAD">C$ CAD</option>
                      </select>
                      <input
                        type="number"
                        value={quoteForm.quote_amount}
                        onChange={e => setQuoteForm(f => ({ ...f, quote_amount: e.target.value }))}
                        placeholder="0.00"
                        min="0.01"
                        step="0.01"
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Message to Customer (optional)</label>
                    <textarea
                      value={quoteForm.message}
                      onChange={e => setQuoteForm(f => ({ ...f, message: e.target.value }))}
                      placeholder="e.g. Includes product cost, EMS shipping to USA, and service fee."
                      rows={3}
                    />
                  </div>

                  <div className="paypal-invoice-note">
                    <span>ℹ️</span>
                    <span>A PayPal invoice will be created and sent directly to the customer's email. They can pay with PayPal, credit card, or debit card.</span>
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={closeQuoteModal}>
                {quoteResult?.success ? 'Close' : 'Cancel'}
              </button>
              {!quoteResult?.success && (
                <button className="btn btn-paypal" onClick={sendQuote} disabled={quoteLoading}>
                  {quoteLoading
                    ? <><span className="spinner"></span> Sending...</>
                    : <><span style={{ fontWeight: 800 }}>Pay</span><span style={{ fontWeight: 800, opacity: 0.8 }}>Pal</span>&nbsp;Send Invoice</>
                  }
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}